import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateEmbryo } from '../contexts/embryos';
import { validateEmbryo } from '../utils/validate';
import { buildEmbryoPayload } from '../utils/payload';
import EmbryoFields from './fields';

import '../../../common/styles/catalog.css';

// Alta de embrión EXTERNO/preservado (los OPU nacen del clivage, no acá). Patrón
// catálogo: estado local → validate → mutate → toast → navegar. La respuesta es el
// envoltorio { embryo, discarded } (0025): si el estadio es DG no se crea registro.
const EMPTY = {
    semen: null,
    donorMode: 'existing',
    donor: null,
    donor_name: '', donor_race_id: '', donor_register: '',
    code: '',
    preservation: 'vitrification',
    stage: '', sex: '', quality: '', remarks: '',
};

export default function CreateEmbryoForm() {
    const navigate = useNavigate();
    const { mutateAsync, isPending } = useCreateEmbryo();
    const [form, setForm] = useState(EMPTY);
    const [localErrors, setLocalErrors] = useState({});
    const [serverFields, setServerFields] = useState({});

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (localErrors[field] || serverFields[field]) {
            setLocalErrors((prev) => ({ ...prev, [field]: undefined }));
            setServerFields((prev) => ({ ...prev, [field]: undefined }));
        }
    };
    const fieldError = (field) => localErrors[field] || serverFields[field];

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateEmbryo(form);
        if (Object.keys(errors).length) { setLocalErrors(errors); toast.error('Revisá los campos marcados'); return; }
        setLocalErrors({}); setServerFields({});

        try {
            const res = await mutateAsync(buildEmbryoPayload(form));
            // Envoltorio 0025: estadio DG → { embryo: null, discarded: 1 }.
            if (res?.discarded || !res?.embryo) {
                toast('Estadio DG (degenerado): se descarta, no se creó registro.');
                navigate('/admin/embryos');
                return;
            }
            toast.success('Embrión creado');
            navigate(`/admin/embryos/${res.embryo.id}`);
        } catch (err) {
            if (err?.status === 409) { setServerFields({ code: 'El código de la pajuela o el registro de la donante ya existe.' }); toast.error('Código o registro duplicado'); }
            else if (err?.fields) { setServerFields(err.fields); toast.error('Revisá los campos marcados'); }
            else toast.error(err?.message || 'No se pudo crear el embrión');
        }
    };

    const canSubmit = !isPending && !!form.semen && !!form.code.trim()
        && (form.donorMode === 'new' ? (!!form.donor_name.trim() && !!form.donor_race_id) : !!form.donor);

    return (
        <form className="cat-form" onSubmit={handleSubmit}>
            <header className="cat-form__header">
                <Link to="/admin/embryos" className="cat-form__back">Banco de embriones</Link>
                <h1 className="cat-form__title">Nuevo embrión</h1>
                <span className="cat-form__subtitle">Embrión externo / preservado (los de OPU se crean desde el clivage).</span>
            </header>

            <EmbryoFields form={form} setField={setField} fieldError={fieldError} disabled={isPending} />

            <footer className="cat-form__footer">
                <Link to="/admin/embryos" className="cat-form__cancel">Cancelar</Link>
                <button type="submit" className="cat-form__submit" disabled={!canSubmit}>
                    {isPending ? 'Creando…' : 'Crear embrión'}
                </button>
            </footer>
        </form>
    );
}
